import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Paperclip } from 'lucide-react';
import { useTaskTimeoutModalStore } from '@/entities/task/model/stores/task-timeout-modal';
import { XMarkIcon } from '@/components/icons/xMark';
import { DocumentIcon } from '@/components/icons/documentIcon';
import { Alarm } from '@/components/icons/alarmIcon';
import { Continue } from '@/components/icons/continueIcon';
import { useElapsedTimeActions } from '@/features/tasks/model/elapsed-times/useElapsedTimeActions';
import { cloneTask, completeTask } from '@/entities/task/api/tasks';
import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import { buildCommentFiles } from '@/shared/utils/fileToBase64Content';
import { createComment } from '@/entities/task/api/comments';
import { addChatResult } from '@/entities/task/api/chat';
import { useQueryClient } from '@tanstack/react-query';
import {
  invalidateTaskCaches,
  useAllTasks,
} from '@/features/tasks/model/useTaskActions';
import { pad2 } from '@/shared/lib/pad2';

function onlyDigits(val: string) {
  return val.replace(/\D/g, '');
}

function clampIntFromStringDigits(val: string, min: number, max: number) {
  const n = Number(onlyDigits(val));
  if (!Number.isFinite(n)) {
    return min;
  }
  return Math.min(max, Math.max(min, n));
}

function formatHHMMFromDigits(raw: string) {
  const d = onlyDigits(raw).slice(0, 4);
  const hhRaw = d.slice(0, 2);
  const mmRaw = d.slice(2, 4);

  const hh = hhRaw.length ? pad2(clampIntFromStringDigits(hhRaw, 0, 99)) : '00';
  const mm = mmRaw.length ? pad2(clampIntFromStringDigits(mmRaw, 0, 59)) : '00';

  return `${hh}:${mm}`;
}

type AttachedFile = {
  file: File;
  id: string;
};

function makeFileId(f: File) {
  return `${f.name}-${f.size}-${f.lastModified}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export function TimeUpModal() {
  const isOpen = useTaskTimeoutModalStore((s) => s.isOpen);
  const taskId = useTaskTimeoutModalStore((s) => s.taskId);
  const close = useTaskTimeoutModalStore((s) => s.close);

  const getCurrentElapsed = useTaskTimerStore((s) => s.getCurrentElapsed);
  const resetTaskTimer = useTaskTimerStore((s) => s.resetTaskTimer);
  const { createElapsedTime } = useElapsedTimeActions();
  const queryClient = useQueryClient();

  const { data: allTasks = [] } = useAllTasks();
  const taskTitle = useMemo(() => {
    if (!taskId) {
      return '';
    }
    const found = allTasks.find((t) => String(t.id) === String(taskId));
    return found?.title ?? '';
  }, [allTasks, taskId]);

  const [extendReason, setExtendReason] = useState('');
  const [finishResult, setFinishResult] = useState('');

  const [timeHHMM, setTimeHHMM] = useState('00:00');

  const [extendFiles, setExtendFiles] = useState<AttachedFile[]>([]);
  const [finishFiles, setFinishFiles] = useState<AttachedFile[]>([]);

  const extendInputRef = useRef<HTMLInputElement | null>(null);
  const finishInputRef = useRef<HTMLInputElement | null>(null);

  const [isExtending, setIsExtending] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  function resetAndClose() {
    setExtendReason('');
    setFinishResult('');
    setTimeHHMM('00:00');
    setExtendFiles([]);
    setFinishFiles([]);
    setIsExtending(false);
    setIsFinishing(false);
    close();
  }

  const extendMinutes = useMemo(() => {
    const [hhStr, mmStr] = timeHHMM.split(':');
    const hh = clampIntFromStringDigits(hhStr ?? '0', 0, 99);
    const mm = clampIntFromStringDigits(mmStr ?? '0', 0, 59);
    return hh * 60 + mm;
  }, [timeHHMM]);

  const canExtend = useMemo(() => {
    return extendReason.trim().length > 0 && extendMinutes > 0 && !isExtending;
  }, [extendReason, extendMinutes, isExtending]);

  const canFinish = useMemo(() => {
    return finishResult.trim().length > 0 && !isFinishing;
  }, [finishResult, isFinishing]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        resetAndClose();
      }
    }

    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow || 'unset';
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  if (!isOpen || !taskId) {
    return null;
  }

  async function handleExtend() {
    if (!taskId || !canExtend) {
      return;
    }

    try {
      setIsExtending(true);

      const deadline = new Date(
        Date.now() + extendMinutes * 60 * 1000
      ).toISOString();
      const commentFiles = await buildCommentFiles(extendFiles, 'comment');
      await createComment(String(taskId), {
        POST_MESSAGE: `Причина продления: ${extendReason.trim()}`,
        files: commentFiles,
      });

      await cloneTask(String(taskId), { deadline });
      await completeTask(String(taskId));
      resetTaskTimer(String(taskId));
      await invalidateTaskCaches(queryClient);
      resetAndClose();
    } catch (e) {
      console.error(e);
      setIsExtending(false);
    }
  }

  async function handleFinish() {
    if (!taskId || !canFinish) {
      return;
    }

    try {
      setIsFinishing(true);

      const elapsedMs = getCurrentElapsed(String(taskId));
      const seconds = Math.floor(elapsedMs / 1000);

      await createElapsedTime({
        taskId: Number(taskId),
        seconds,
        comment: finishResult.trim(),
      });

      // Комментарий «Причина завершения» — текстовый; файлы теперь уходят
      // в результат (ниже), а не в комментарий.
      await createComment(String(taskId), {
        POST_MESSAGE: `Причина завершения: ${finishResult.trim()}`,
        files: [],
      });

      // Результат задачи: текст + прикреплённые файлы (multipart). С файлами
      // /chat/results сам постит сообщение в чат и кладёт их в result.fileIds.
      // Фатально: при сбое не теряем файлы молча — завершение не произойдёт.
      const resultFiles = finishFiles.map((f) => f.file);
      await addChatResult(String(taskId), finishResult.trim(), resultFiles);
      queryClient.invalidateQueries({
        queryKey: ['task-results', String(taskId)],
      });

      await completeTask(String(taskId));
      resetTaskTimer(String(taskId));
      await invalidateTaskCaches(queryClient);
      resetAndClose();
    } catch (e) {
      console.error(e);
      setIsFinishing(false);
    }
  }

  function pickExtendFiles() {
    extendInputRef.current?.click();
  }

  function pickFinishFiles() {
    finishInputRef.current?.click();
  }

  function onExtendFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) {
      return;
    }
    const next: AttachedFile[] = Array.from(files).map((f) => ({
      file: f,
      id: makeFileId(f),
    }));
    setExtendFiles((prev) => [...prev, ...next]);
  }

  function onFinishFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) {
      return;
    }
    const next: AttachedFile[] = Array.from(files).map((f) => ({
      file: f,
      id: makeFileId(f),
    }));
    setFinishFiles((prev) => [...prev, ...next]);
  }

  function FileList({ files }: { files: AttachedFile[] }) {
    if (files.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-[5px] min-w-0">
        {files.map((f) => (
          <button
            key={f.id}
            type="button"
            title={f.file.name}
            className="w-fit max-w-full flex items-center gap-[6px] text-left group"
          >
            <DocumentIcon color="#000000" />
            <span className="text-[14px] leading-[130%] tracking-[-0.83px] font-normal text-black underline underline-offset-2 truncate">
              {f.file.name}
            </span>
          </button>
        ))}
      </div>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-md animate-in fade-in-0 duration-300"
        onClick={resetAndClose}
        style={{ cursor: 'pointer' }}
      />

      <div className="absolute inset-0 grid place-items-center">
        <div
          className="w-full max-w-[430px] lg:max-w-[1010px] rounded-[20px] overflow-hidden bg-white backdrop-blur-[2px] animate-in fade-in-0 zoom-in-95 duration-300 ease-out"
          style={{
            boxShadow:
              '0px 0px 11px rgba(51, 193, 231, 0.4), 0px 0px 13px rgba(51, 193, 231, 0.4)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-[20px] py-[31px] lg:px-[24px] lg:py-[40px]">
            <div className="flex items-start justify-between gap-6">
              <div className="flex flex-col gap-1 min-w-0">
                {taskTitle && (
                  <h2 className="text-[#14222C] text-[18px] lg:text-[22px] leading-[130%] tracking-[-0.5px] font-semibold break-words">
                    {taskTitle}
                  </h2>
                )}
                <p className="text-[#14222C] text-[16px] lg:text-[18px] leading-[130%] tracking-[-0.5px] font-normal">
                  Время на задачу вышло. Продлеваем или завершаем?
                </p>
              </div>

              <button
                type="button"
                onClick={resetAndClose}
                className="rounded-full p-2 hover:bg-black/5 transition shrink-0"
                aria-label="Закрыть"
                title="Закрыть"
              >
                <XMarkIcon width={12} height={12} color="#14222C" />
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Продление */}
              <div className="flex flex-col gap-4">
                <span className="text-black/60 text-[16px] lg:text-[20px] leading-[130%] font-normal">
                  Продлить
                </span>

                <div className="rounded-[8px] bg-white shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] p-[12px] min-h-[78px] lg:min-h-[160px] flex flex-col gap-[11px]">
                  <textarea
                    placeholder="Укажите причину продления (обязательное поле)"
                    value={extendReason}
                    onChange={(e) => setExtendReason(e.target.value)}
                    className="w-full flex-1 min-h-[36px] lg:min-h-[80px] bg-transparent border-0 outline-none resize-none text-[#14222C] text-[14px] leading-[130%] tracking-[-0.5px] placeholder:text-black/60"
                  />

                  <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <FileList files={extendFiles} />
                    </div>

                    <button
                      type="button"
                      onClick={pickExtendFiles}
                      className="shrink-0 text-black/60 hover:text-black transition"
                      title="Прикрепить файл"
                      aria-label="Прикрепить файл"
                    >
                      <Paperclip size={18} />
                    </button>
                  </div>
                </div>

                <input
                  ref={extendInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => onExtendFilesSelected(e.target.files)}
                />

                <div className="flex flex-wrap items-center justify-center lg:justify-end gap-4 lg:gap-5">
                  <div className="flex items-center gap-[14px] rounded-[17px] bg-[#F8F7FC] shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] px-[11px] h-[44px]">
                    <span className="text-[#14222C] text-[16px] tracking-[-0.83px] leading-[130%] font-normal whitespace-nowrap">
                      Введите время:
                    </span>

                    <input
                      value={timeHHMM}
                      inputMode="numeric"
                      onChange={(e) => {
                        const digits = onlyDigits(e.target.value);
                        setTimeHHMM(formatHHMMFromDigits(digits));
                      }}
                      onFocus={(e) => {
                        requestAnimationFrame(() => {
                          const el = e.currentTarget;
                          const p = el.value.length;
                          el.setSelectionRange(p, p);
                        });
                      }}
                      className="
                        w-[42px]
                        bg-transparent
                        underline
                        rounded-none
                        px-0
                        py-0
                        text-black
                        text-[16px]
                        leading-[130%]
                        tracking-[-0.83px]
                        font-normal
                        tabular-nums
                        outline-none
                      "
                      aria-label="Время в формате HH:MM"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleExtend}
                    disabled={!canExtend}
                    style={{
                      background:
                        'linear-gradient(257.42deg, #152242 0.84%, #3B4D7F 100.65%)',
                    }}
                    className="
                      flex items-center gap-2
                      h-[44px] px-[17px]
                      rounded-[24px]
                      text-[16px]
                      tracking-[-0.83px]
                      leading-[130%]
                      font-normal
                      text-white
                      disabled:opacity-50
                      disabled:cursor-not-allowed
                    "
                  >
                    <Alarm className="shrink-0" />
                    <span>{isExtending ? 'Продлеваю…' : 'Продлить'}</span>
                  </button>
                </div>
              </div>

              {/* Завершение */}
              <div className="flex flex-col gap-4">
                <span className="text-black/60 text-[16px] lg:text-[20px] leading-[130%] font-normal">
                  Завершить
                </span>

                <div className="rounded-[8px] bg-white shadow-[0_3.54px_12.88px_rgba(0,0,0,0.03)] p-[12px] min-h-[158px] flex flex-col gap-[11px]">
                  <textarea
                    placeholder="Укажите результат работы, подкрепите файл при необходимости (обязательное поле)"
                    value={finishResult}
                    onChange={(e) => setFinishResult(e.target.value)}
                    className="w-full flex-1 min-h-[80px] bg-transparent border-0 outline-none resize-none text-[#14222C] text-[14px] leading-[130%] tracking-[-0.5px] placeholder:text-black/60"
                  />

                  <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <FileList files={finishFiles} />
                    </div>

                    <button
                      type="button"
                      onClick={pickFinishFiles}
                      className="shrink-0 text-black/60 hover:text-black transition"
                      title="Прикрепить файл"
                      aria-label="Прикрепить файл"
                    >
                      <Paperclip size={18} />
                    </button>
                  </div>
                </div>

                <input
                  ref={finishInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => onFinishFilesSelected(e.target.files)}
                />

                <div className="flex items-center justify-center lg:justify-end">
                  <button
                    type="button"
                    onClick={handleFinish}
                    disabled={!canFinish}
                    className="
                      flex items-center gap-2
                      h-[44px] px-[18px]
                      rounded-[24px]
                      text-[16px]
                      tracking-[-0.83px]
                      leading-[130%]
                      font-normal
                      text-white
                      bg-[#57B792]
                      disabled:opacity-50
                      disabled:cursor-not-allowed
                    "
                  >
                    <Continue />
                    <span>{isFinishing ? 'Завершаю…' : 'Завершить'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
